// MarketSwitcher — Shows the active market badge and lets the user switch
// the same page to a different country. Used in ServicePage and IndustryPage.

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { MARKETS_CONFIG, MARKET_CODES } from "../../data/markets";

export function MarketPill({ market }) {
  if (!market) return null;
  return (
    <span className="inline-flex items-center gap-1.5 border border-electric/30 bg-electric/10 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-electric">
      <span aria-hidden>{market.flag}</span>
      Serving {market.shortName}
    </span>
  );
}

export function MarketSwitcher({ slug, currentCode = null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLabel = currentCode
    ? `${MARKETS_CONFIG[currentCode].flag} ${MARKETS_CONFIG[currentCode].name}`
    : "🌍 Global";

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-2 border border-zinc-200 bg-white rounded-xl px-4 py-2.5 text-sm font-semibold text-charcoal hover:border-electric hover:text-electric transition-colors"
        data-testid="market-switcher-button"
      >
        <Globe className="w-4 h-4 text-electric" />
        {currentLabel}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute right-0 mt-2 w-64 rounded-2xl border border-zinc-100 bg-white shadow-card-hover p-2 z-50"
          data-testid="market-switcher-menu"
        >
          <Link
            to={`/${slug}`}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
              !currentCode
                ? "bg-electric text-white"
                : "text-charcoal hover:bg-zinc-50"
            }`}
          >
            🌍 <span className="font-semibold">Global</span>
            <span className="ml-auto text-[10px] uppercase tracking-widest opacity-70">
              English
            </span>
          </Link>
          <div className="my-2 border-t border-zinc-100" />
          {MARKET_CODES.map((c) => {
            const m = MARKETS_CONFIG[c];
            const isActive = currentCode === c;
            return (
              <Link
                key={c}
                to={`/${c}/${slug}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
                  isActive
                    ? "bg-electric text-white"
                    : "text-charcoal hover:bg-zinc-50"
                }`}
              >
                <span aria-hidden>{m.flag}</span>
                <span className="font-semibold">{m.name}</span>
                <span
                  className={`ml-auto text-[10px] uppercase tracking-widest ${
                    isActive ? "text-white/80" : "text-zinc-400"
                  }`}
                >
                  {m.currency.trim()}
                </span>
              </Link>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
